import { useRef, useState, useEffect } from 'react';
import { ArrowLeft, Play, Pause, Clock, Droplets, Info, CheckCircle, Coins } from 'lucide-react';
import { prayerGuides } from '../data/prayers';
import { wuduSteps } from '../data/wudu';
import { useProfile } from '../hooks/useProfile';
import { useFootprint } from '../hooks/useFootprint';
import { SurahDynamic } from './SurahDynamic';

interface PrayerGuideProps {
  prayerName: string;
  onBack: () => void;
}

export function PrayerGuide({ prayerName, onBack }: PrayerGuideProps) {
  const { profile } = useProfile();
  const { mintCoin, logDeed } = useFootprint(); // Import logDeed
  const guide = prayerGuides[prayerName] || prayerGuides['Fajr'];
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState<'prayer' | 'wudu'>('prayer');
  const [currentTime, setCurrentTime] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [timer, setTimer] = useState<number | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [stepProgress, setStepProgress] = useState(0); // 0-100 for silent steps
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Reset when prayer changes
  useEffect(() => {
    setActiveStepIndex(0);
    setIsPlaying(false);
    setCurrentTime(0);
    setTimer(null);
    setStepProgress(0);
    setIsCompleted(false);
  }, [prayerName]);

  // Audio Time Update Handler
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      
      // Auto-advance logic for audio steps
      const currentStep = guide.steps[activeStepIndex];
      if (currentStep?.audioEnd !== undefined && audio.currentTime >= currentStep.audioEnd) {
         setActiveStepIndex(prev => prev + 1);
      }
    };

    const handleEnded = () => {
        setActiveStepIndex(prev => prev + 1);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [activeStepIndex, guide]);

  // Master Sequencer
  useEffect(() => {
    if (!isPlaying) {
      if (timer) clearTimeout(timer);
      setStepProgress(0);
      return;
    }

    const currentStep = guide.steps[activeStepIndex];
    if (!currentStep) {
        setIsPlaying(false); // End of prayer
        setIsCompleted(true);
        
        // Log as a deed (Updates Heatmap + Mints Coin + Logs Transaction)
        // We use logDeed instead of direct mintCoin to ensure it shows on the user's daily activity chart.
        // We pass a custom description and coin amount (if logDeed supports it, otherwise it defaults to 1).
        
        // Wait, logDeed in useFootprint currently hardcodes amount to 1.
        // We should just call logDeed for the heatmap, and maybe mint EXTRA coins if needed?
        // Or update logDeed to accept amount.
        
        // For now, let's call logDeed to ensure it hits the blockchain AND heatmap.
        logDeed(`Completed ${guide.name} Prayer`); 
        
        // Since logDeed only mints 1 coin, and we want 5 for a prayer:
        // We can mint 4 more manually.
        mintCoin('prayer', 'Prayer Bonus', 4);
        
        return;
    }

    // Scroll to active step
    stepRefs.current[activeStepIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Handle Audio Steps
    if (currentStep.audioStart !== undefined || currentStep.customAudioUrl) {
        setStepProgress(0); // Audio uses its own progress in SurahDynamic or implicitly via time
        
        if (audioRef.current) {
            // Force user interaction to allow audio on mobile
            if (audioRef.current.paused && activeStepIndex === 0) {
                 audioRef.current.load();
            }

            const targetSrc = currentStep.customAudioUrl || guide.audioUrl!;
            const targetTime = currentStep.audioStart || 0;
            
            // Check if we need to change source or seek
            if (audioRef.current.src !== targetSrc) {
                audioRef.current.src = targetSrc;
                audioRef.current.currentTime = targetTime;
                audioRef.current.play().catch(e => console.error("Audio playback failed:", e));
            } else {
                // Same source, ensure we are at the right time if starting a new segment
                // Tolerance of 1s to prevent seeking if just continuing
                if (Math.abs(audioRef.current.currentTime - targetTime) > 1 && !currentStep.customAudioUrl) {
                     audioRef.current.currentTime = targetTime;
                }
                
                if (audioRef.current.paused) {
                    audioRef.current.play().catch(e => console.error("Audio playback failed:", e));
                }
            }
        }
    } 
    // Handle Silent/Movement Steps (Timer)
    else if (currentStep.duration) {
        // Ensure audio is paused during silent steps
        if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
        }

        // Add 3s delay to prevent racing ahead
        const durationWithDelay = (currentStep.duration * 1000) + 3000;
        const startTime = Date.now();

        // Progress Interval
        const progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const pct = Math.min(100, (elapsed / durationWithDelay) * 100);
            setStepProgress(pct);
        }, 100);

        const timeout = setTimeout(() => {
            clearInterval(progressInterval);
            setStepProgress(100);
            setActiveStepIndex(prev => prev + 1);
        }, durationWithDelay);
        
        setTimer(Number(timeout));

        return () => clearInterval(progressInterval);
    }

  }, [activeStepIndex, isPlaying, guide]);

  // Handle Play/Pause Toggle
  const toggleSession = () => {
    if (isPlaying) {
        setIsPlaying(false);
        if (audioRef.current) audioRef.current.pause();
        if (timer) clearTimeout(timer);
    } else {
        // If restarting from end, reset
        if (activeStepIndex >= guide.steps.length) {
            setActiveStepIndex(0);
        }
        setIsPlaying(true);
        // Explicitly call play() on button click to unlock audio context
        if (audioRef.current && audioRef.current.paused) {
            audioRef.current.play().catch(console.error);
        }
    }
  };

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-700">
        <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 animate-bounce">
            <CheckCircle className="w-12 h-12 text-emerald-400" />
        </div>
        <h2 className="text-3xl font-bold font-serif mb-2 text-emerald-100">Alhamdulillah!</h2>
        <p className="text-slate-400 mb-8">You have completed the {guide.name} prayer.</p>
        
        <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-amber-500/30 p-6 rounded-2xl mb-8 flex items-center gap-4 w-full max-w-sm">
            <div className="bg-amber-500 p-3 rounded-full shadow-[0_0_20px_rgba(245,158,11,0.4)]">
                <Coins className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
                <h3 className="font-bold text-amber-200 text-lg">+5 Habl Coins</h3>
                <p className="text-xs text-amber-400/70">Minted to your immutable ledger</p>
            </div>
        </div>

        <button 
            onClick={onBack}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-8 rounded-xl transition-all w-full max-w-sm"
        >
            Return Home
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${profile.settings.darkMode ? 'bg-slate-900 text-white' : 'bg-amber-50/30 text-slate-900'} flex flex-col pb-32 transition-colors duration-300`}>
      {/* Header */}
      <div className={`${profile.settings.darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-amber-100'} px-6 py-4 pt-safe-top border-b sticky top-0 z-10 shadow-sm flex items-center justify-between`}>
        <button 
          onClick={onBack}
          className={`p-2 -ml-2 rounded-full ${profile.settings.darkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-amber-50 text-slate-600'} transition-colors`}
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="text-center">
          <span className={`text-xs uppercase tracking-widest ${profile.settings.darkMode ? 'text-emerald-400' : 'text-emerald-600'} font-semibold mb-1 block`}>Prayer Guide</span>
          <h2 className="text-2xl font-bold font-serif">{guide.name}</h2>
          <p className={`text-sm ${profile.settings.darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{guide.rakyat} Raka'at • {guide.type}</p>
        </div>
        <div className="w-10"></div> {/* Spacer */}
      </div>

      {/* Content */}
      <div className="flex-1 p-6 max-w-md mx-auto w-full space-y-8">

        {/* Tab Switcher */}
        <div className={`${profile.settings.darkMode ? 'bg-slate-800' : 'bg-stone-100'} p-1 rounded-xl flex gap-1`}>
            <button 
                onClick={() => setActiveTab('prayer')}
                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'prayer' ? (profile.settings.darkMode ? 'bg-slate-700 text-emerald-400 shadow-sm' : 'bg-white text-emerald-600 shadow-sm') : (profile.settings.darkMode ? 'text-slate-400' : 'text-slate-500')}`}
            >
                Prayer Guide
            </button>
            <button 
                onClick={() => setActiveTab('wudu')}
                className={`flex-1 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${activeTab === 'wudu' ? (profile.settings.darkMode ? 'bg-slate-700 text-sky-400 shadow-sm' : 'bg-white text-sky-600 shadow-sm') : (profile.settings.darkMode ? 'text-slate-400' : 'text-slate-500')}`}
            >
                <Droplets className="w-4 h-4" /> Wudu Guide
            </button>
        </div>
        
        {activeTab === 'prayer' ? (
          <>
            {/* Info Card */}
            <div className={`${profile.settings.darkMode ? 'bg-emerald-900/20 border-emerald-500/20' : 'bg-emerald-50 border-emerald-100'} border p-4 rounded-xl flex items-start gap-3`}>
                <Clock className={`w-5 h-5 ${profile.settings.darkMode ? 'text-emerald-400' : 'text-emerald-600'} mt-0.5`} />
                <div>
                    <h4 className={`font-bold ${profile.settings.darkMode ? 'text-emerald-100' : 'text-emerald-900'} text-sm`}>Time Window</h4>
                    <p className={`${profile.settings.darkMode ? 'text-emerald-300' : 'text-emerald-700'} text-sm`}>{guide.time}</p>
                </div>
            </div>

            {/* Note about Audio/Steps */}
            <div className={`text-xs p-3 rounded-lg border ${profile.settings.darkMode ? 'bg-slate-800 border-slate-700 text-slate-400' : 'bg-stone-50 border-stone-200 text-stone-600'} flex gap-2`}>
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-70" />
                <p>
                    All prayers share the same core recitations (Al-Fatiha). The main difference is the number of Raka'at (units) and the time of day.
                </p>
            </div>

            {/* Steps */}
            <div className="space-y-8 pb-32">
                {guide.steps.map((step, idx) => {
                    const isActive = idx === activeStepIndex;

                    return (
                    <div 
                        key={idx} 
                        ref={(el) => stepRefs.current[idx] = el}
                        onClick={() => {
                            if (!isPlaying) setActiveStepIndex(idx);
                        }}
                        className={`text-center space-y-4 border-b ${profile.settings.darkMode ? 'border-slate-800' : 'border-stone-50'} pb-8 last:border-0 transition-all duration-500 ${isActive ? 'scale-105 opacity-100' : 'opacity-40 blur-[1px] hover:opacity-70 hover:blur-0 cursor-pointer'}`}
                    >
                        {/* Step Badge */}
                        <div className="flex justify-center mb-2">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${isActive ? 'bg-emerald-500 text-white' : (profile.settings.darkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700')}`}>
                                {idx + 1}
                            </span>
                        </div>

                        {/* Instruction */}
                        <h4 className={`font-bold text-lg ${profile.settings.darkMode ? 'text-white' : 'text-slate-900'}`}>{step.instruction}</h4>
                        
                        {/* Dynamic Content or Static Text */}
                        {step.isDynamic && step.surahId ? (
                            <SurahDynamic 
                                surahId={step.surahId}
                                currentTime={currentTime} 
                                isPlaying={isPlaying} 
                                isDarkMode={profile.settings.darkMode} 
                            />
                        ) : (
                            <>
                                {/* Arabic */}
                                {step.arabic && (
                                    <p className={`text-3xl font-serif leading-loose ${isActive ? 'text-emerald-500 dark:text-emerald-300' : (profile.settings.darkMode ? 'text-emerald-100' : 'text-slate-800')}`} dir="rtl" style={{ fontFamily: 'Traditional Arabic, serif' }}>
                                        {step.arabic}
                                    </p>
                                )}
                                
                                {/* Transliteration */}
                                {step.transliteration && (
                                    <p className={`font-medium text-sm ${profile.settings.darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                                        {step.transliteration}
                                    </p>
                                )}
                                
                                {/* Translation */}
                                {step.translation && (
                                    <p className={`text-sm italic ${profile.settings.darkMode ? 'text-slate-400' : 'text-stone-500'}`}>
                                        "{step.translation}"
                                    </p>
                                )}

                                {/* Progress Bar for Silent Steps */}
                                {isActive && isPlaying && step.duration && (
                                    <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full mt-6 overflow-hidden max-w-xs mx-auto">
                                        <div 
                                            className="h-full bg-emerald-500 transition-all duration-100 ease-linear"
                                            style={{ width: `${stepProgress}%` }}
                                        ></div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Visual Illustration */}
                        {step.image && (
                            <div className="mt-4 mx-auto w-full max-w-[200px] flex justify-center">
                                <img 
                                    src={`assets/prayer/${
                                        step.image === 'takbir' ? 'folding-up.png' :
                                        step.image === 'ruku' ? 'bowing.png' :
                                        step.image === 'standing' ? 'folding-hands.png' :
                                        step.image === 'sujood' ? 'prostrating.png' :
                                        step.image === 'sitting' ? 'suplicating.png' : 'folding-hands.png'
                                    }`}
                                    alt={step.image}
                                    onError={(e) => {
                                        // Fallback to placeholder if image fails to load
                                        console.error('Image failed to load:', e.currentTarget.src);
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                    className={`h-40 object-contain drop-shadow-md ${profile.settings.darkMode ? 'opacity-90 invert' : ''}`}
                                />
                                {/* Fallback Placeholder (Hidden by default) */}
                                <div className="hidden w-full h-32 rounded-lg flex items-center justify-center border border-slate-700 bg-slate-800">
                                     <div className="text-center">
                                        <div className="w-8 h-8 mx-auto mb-2 text-slate-600">👤</div>
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-600">Visual: {step.image}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>
          </>
        ) : (
          /* Wudu Tab */
          <div className="space-y-4">
             <div className={`${profile.settings.darkMode ? 'bg-sky-900/20 border-sky-500/20' : 'bg-sky-50 border-sky-100'} border p-4 rounded-xl flex items-start gap-3`}>
                <Info className={`w-5 h-5 ${profile.settings.darkMode ? 'text-sky-400' : 'text-sky-600'} mt-0.5`} />
                <div>
                    <h4 className={`font-bold ${profile.settings.darkMode ? 'text-sky-100' : 'text-sky-900'} text-sm`}>Before Prayer</h4>
                    <p className={`${profile.settings.darkMode ? 'text-sky-300' : 'text-sky-700'} text-sm`}>You must be in a state of purity (Wudu) before performing Salah.</p>
                </div>
            </div>

            <div className="space-y-8">
                {wuduSteps.map((step) => (
                    <div key={step.id} className={`text-center space-y-4 border-b ${profile.settings.darkMode ? 'border-slate-800' : 'border-stone-50'} pb-8 last:border-0`}>
                        <div className="flex justify-center mb-2">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${profile.settings.darkMode ? 'bg-sky-900/50 text-sky-300' : 'bg-sky-100 text-sky-600'}`}>
                                {step.id}
                            </span>
                        </div>

                        {/* Visual Placeholder */}
                        <div className={`mx-auto w-32 h-32 rounded-full flex items-center justify-center mb-4 ${profile.settings.darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                             <Droplets className={`w-12 h-12 ${profile.settings.darkMode ? 'text-sky-800' : 'text-sky-200'}`} />
                        </div>

                        <h4 className={`font-bold text-lg ${profile.settings.darkMode ? 'text-white' : 'text-slate-900'}`}>{step.instruction}</h4>
                        
                        <p className={`text-sm leading-relaxed max-w-xs mx-auto ${profile.settings.darkMode ? 'text-slate-300' : 'text-stone-600'}`}>
                            {step.detail}
                        </p>
                    </div>
                ))}
            </div>
          </div>
        )}

        {/* Audio Player (Hidden but functional) */}
        {guide.audioUrl && (
             <audio 
             ref={audioRef} 
             src={guide.audioUrl} 
             onEnded={() => setIsPlaying(false)} 
           />
        )}
      </div>

      {/* Floating Action Bar - Only for Prayer Tab */}
      {activeTab === 'prayer' && (
        <div className={`fixed bottom-0 left-0 w-full border-t p-6 safe-area-bottom shadow-lg z-20 ${profile.settings.darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-stone-100'}`}>
            <div className="max-w-md mx-auto space-y-2">
            {/* Reciter Credit - ALWAYS VISIBLE ABOVE BUTTONS */}
            <div className="text-center space-y-1 pb-3">
                <p className={`text-xs font-medium ${profile.settings.darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    Recitation by Mishary Rashid Alafasy
                </p>
                <p className="text-[10px] text-slate-400 opacity-60">
                    Sourced from Mp3Quran.net • v2.1
                </p>
            </div>

            <button 
                onClick={toggleSession}
                className={`w-full font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg ${isPlaying ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-900/20'}`}
            >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {isPlaying ? "Pause Session" : (activeStepIndex > 0 ? "Resume Session" : "Start Prayer Session")}
            </button>
            </div>
        </div>
      )}
    </div>
  );
}